import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AssessmentRecipientRow,
  fetchAssessmentRecipients,
  addAssessmentRecipient,
  removeAssessmentRecipient,
  clearAssessmentRecipients,
  generateMassTokens,
  sendMassTokens,
  sendSingleToken,
} from './assessmentRecipientsApi';

/**
 * Clave para estado de envío individual por encuesta + destinatario (evita arrastre entre assessments).
 */
export function assessmentSendSingleStateKey(assessmentId: string, recipientId: string): string {
  return `${assessmentId}::${recipientId}`;
}

interface AssessmentRecipientsState {
  items: Record<string, AssessmentRecipientRow[]>; // keyed by assessmentId
  loading: boolean;
  sending: boolean;
  /** Claves `assessmentId::recipientId` (recipientId = id de fila / externalRecipientId). */
  sendingSingle: Record<string, boolean>;
  sendSingleResult: Record<string, { success: boolean; message?: string }>;
  error: string | null;
}

const initialState: AssessmentRecipientsState = {
  items: {},
  loading: false,
  sending: false,
  sendingSingle: {},
  sendSingleResult: {},
  error: null,
};

export const loadRecipients = createAsyncThunk<AssessmentRecipientRow[], string>(
  'assessmentRecipients/load',
  async (assessmentId) => {
    const response = await fetchAssessmentRecipients(assessmentId);
    return response.data;
  }
);

export const addRecipient = createAsyncThunk<
  AssessmentRecipientRow,
  { assessmentId: string; externalRecipientId: string }
>('assessmentRecipients/add', async ({ assessmentId, externalRecipientId }) => {
  return addAssessmentRecipient(assessmentId, { externalRecipientId });
});

export const removeRecipient = createAsyncThunk<
  { assessmentId: string; recipientId: string },
  { assessmentId: string; recipientId: string },
  { rejectValue: string }
>(
  'assessmentRecipients/remove',
  async ({ assessmentId, recipientId }, { rejectWithValue }) => {
    try {
      await removeAssessmentRecipient(assessmentId, recipientId);
      return { assessmentId, recipientId };
    } catch (error: any) {
      return rejectWithValue(
        typeof error?.message === 'string' ? error.message : 'No se pudo quitar el destinatario'
      );
    }
  }
);

export const clearRecipients = createAsyncThunk<string, string, { rejectValue: string }>(
  'assessmentRecipients/clear',
  async (assessmentId, { rejectWithValue }) => {
    try {
      await clearAssessmentRecipients(assessmentId);
      return assessmentId;
    } catch (error: any) {
      return rejectWithValue(
        typeof error?.message === 'string' ? error.message : 'No se pudieron quitar los destinatarios'
      );
    }
  }
);

export const generateAndSend = createAsyncThunk<
  { assessmentId: string; sent: number; failed: number; recipients: AssessmentRecipientRow[] },
  string,
  { rejectValue: string }
>('assessmentRecipients/generateAndSend', async (assessmentId, { rejectWithValue }) => {
  try {
    // Step 1: generate mass tokens
    await generateMassTokens(assessmentId);
    // Step 2: send mass tokens
    const result = await sendMassTokens(assessmentId);
    // Step 3: refresh recipients to reflect token status
    const recipientsResponse = await fetchAssessmentRecipients(assessmentId);
    return { assessmentId, sent: result.sent, failed: result.failed, recipients: recipientsResponse.data };
  } catch (error: any) {
    return rejectWithValue(
      typeof error?.message === 'string' ? error.message : 'Error en el envío masivo'
    );
  }
});

export const sendSingle = createAsyncThunk<
  { assessmentId: string; recipientId: string; result: { success: boolean; sent?: boolean; alreadySent?: boolean; message?: string; error?: string } },
  { assessmentId: string; recipientId: string },
  { rejectValue: string }
>('assessmentRecipients/sendSingle', async ({ assessmentId, recipientId }, { rejectWithValue }) => {
  try {
    const result = await sendSingleToken(assessmentId, recipientId);
    return { assessmentId, recipientId, result };
  } catch (error: any) {
    return rejectWithValue(
      typeof error?.message === 'string' ? error.message : 'Error al enviar el email'
    );
  }
});

const assessmentRecipientsSlice = createSlice({
  name: 'assessmentRecipients',
  initialState,
  reducers: {
    setRecipientsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    clearRecipientsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loadRecipients
      .addCase(loadRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRecipients.fulfilled, (state, action) => {
        state.loading = false;
        const assessmentId = action.meta.arg;
        state.items[assessmentId] = action.payload;
        const prefix = `${assessmentId}::`;
        for (const k of Object.keys(state.sendingSingle)) {
          if (k.startsWith(prefix)) delete state.sendingSingle[k];
        }
        for (const k of Object.keys(state.sendSingleResult)) {
          if (k.startsWith(prefix)) delete state.sendSingleResult[k];
        }
      })
      .addCase(loadRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'No se pudieron cargar los destinatarios';
      })
      // addRecipient
      .addCase(addRecipient.fulfilled, (state, action) => {
        const assessmentId = action.meta.arg.assessmentId;
        if (!action.payload) return;
        const current = state.items[assessmentId] || [];
        state.items[assessmentId] = [...current, action.payload];
      })
      .addCase(addRecipient.rejected, (state, action) => {
        state.error = action.error.message || 'No se pudo agregar el destinatario';
      })
      // removeRecipient
      .addCase(removeRecipient.fulfilled, (state, action) => {
        const { assessmentId, recipientId } = action.payload;
        const current = state.items[assessmentId];
        if (current) {
          state.items[assessmentId] = current.filter((r) => r.id !== recipientId);
        }
      })
      .addCase(removeRecipient.rejected, (state, action) => {
        if (action.payload) state.error = action.payload;
      })
      // clearRecipients
      .addCase(clearRecipients.fulfilled, (state, action) => {
        state.items[action.payload] = [];
      })
      .addCase(clearRecipients.rejected, (state, action) => {
        if (action.payload) state.error = action.payload;
      })
      // generateAndSend
      .addCase(generateAndSend.pending, (state) => {
        state.sending = true;
        state.error = null;
      })
      .addCase(generateAndSend.fulfilled, (state, action) => {
        state.sending = false;
        const { assessmentId, recipients } = action.payload;
        state.items[assessmentId] = recipients;
        const prefix = `${assessmentId}::`;
        for (const k of Object.keys(state.sendingSingle)) {
          if (k.startsWith(prefix)) delete state.sendingSingle[k];
        }
        for (const k of Object.keys(state.sendSingleResult)) {
          if (k.startsWith(prefix)) delete state.sendSingleResult[k];
        }
      })
      .addCase(generateAndSend.rejected, (state, action) => {
        state.sending = false;
        if (action.payload) state.error = action.payload;
      })
      // sendSingle
      .addCase(sendSingle.pending, (state, action) => {
        const { assessmentId, recipientId } = action.meta.arg;
        const key = assessmentSendSingleStateKey(assessmentId, recipientId);
        state.sendingSingle[key] = true;
        state.sendSingleResult[key] = { success: true };
      })
      .addCase(sendSingle.fulfilled, (state, action) => {
        const { assessmentId, recipientId } = action.meta.arg;
        const key = assessmentSendSingleStateKey(assessmentId, recipientId);
        state.sendingSingle[key] = false;
        const result = action.payload?.result;
        if (!result) {
          state.sendSingleResult[key] = {
            success: false,
            message: 'Respuesta inválida del servidor',
          };
          return;
        }
        const ok =
          Boolean(result.success) &&
          (result.alreadySent === true || result.sent === true || (result.sent !== false && !result.error));
        state.sendSingleResult[key] = {
          success: ok,
          message:
            result.message ||
            (result.alreadySent ? 'Ya enviado' : ok ? 'Enviado' : result.error || 'Error al enviar'),
        };
      })
      .addCase(sendSingle.rejected, (state, action) => {
        const { assessmentId, recipientId } = action.meta.arg;
        const key = assessmentSendSingleStateKey(assessmentId, recipientId);
        state.sendingSingle[key] = false;
        state.sendSingleResult[key] = {
          success: false,
          message: action.payload || 'Error al enviar',
        };
      });
  },
});

export const { setRecipientsLoading, clearRecipientsError } = assessmentRecipientsSlice.actions;

export default assessmentRecipientsSlice.reducer;
